import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {CustomAutocomplete} from "ui";
import {getTooltipText} from "views/ProcessDetails/utils/getTooltipText";
import {useState} from "react";
import useChangeDAteAndAutocomplete from "views/ProcessDetails/hooks/useChangeDAteAndAutocomplete";
import {AutoCompleteData} from "ui/CustomAutoComplete/CustomAutoComplete";
import useHandleFieldLists from "views/ProcessDetails/hooks/useHandleFieldLists";
import {
  GetIncotermsList,
  GetTransportCompaniesList,
} from "services/api_v2/Processes/Processes.service";
import {
  TypePath as TypePathEnum,
  ModalityPickup as ModalityPickupEnum,
  TypeLoad as TypeLoadEnum,
  ModalityDelivery as ModalityDeliveryEnum,
} from "util/globalEnums";
import {getPorts} from "services/api_v2/Tasks/tasks.service";
import useHandleBoardingOptions from "./components/ProcessBoarding/useHandleBoardingOptions";
import {
  GetPersonsByTypes,
  PersonTypes,
} from "services/api_v2/Persons/Persons.service";
import {
  getDestinationVoyageIndex,
  getFinalDestinationVoyageIndex,
  getInitialVoyageIndex,
} from "../utils/getVoyageIndex";
import {ProcessDatePicker} from "components";
import {Grid} from "@mui/system";
import ProcessTextField from "views/ProcessDetails/refactor/components/ProcessTextField";
import {ProcessSelect} from "views/ProcessDetails/refactor/components";
import {Process} from "views/ProcessDetails/refactor/types/process.types";
import {Voyage} from "views/ProcessDetails/Process.types";
import {
  ID_COLLECT_VOYAGE,
  ID_FINAL_DESTINATION_VOYAGE,
  ID_INITIAL_VOYAGE,
  ID_TRANSHIPMENT_TYPE_VOYAGE,
} from "views/ProcessDetails/utils/constants";
import {getLocationType} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/utils/getLocationType";

type Props = {
  xs?: number;
  title?: string;
};

const formatTransportCompanyFn = (data) => {
  return data?.data?.map((item) => ({
    label: `${item?.person?.name} - ${item?.idPerson}`,
    id: item?.person?.id,
  }));
};
const formatOptionsFn = (data) => {
  return data?.data?.map((item) => ({
    label: `${item?.name} - ${item?.id}`,
    id: item?.id,
  }));
};

const Origin = (props: Props) => {
  const {xs = 2, title = "Porto de Origem"} = props;
  const {control, getValues} = useFormContext<Process>();
  const {fields} = useFieldArray({
    name: "boarding.voyages",
    keyName: "formId",
    control: control,
  });

  const [boardingOptions, setBoardingOptions] = useState(
    [] as AutoCompleteData[],
  );
  const [search, setSearch] = useState("");

  const {getOptions, onBottomScroll, isLoadingOptions} =
    useHandleBoardingOptions();
  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();

  const getVoyageIndex = () => {
    const voyageIndex = getInitialVoyageIndex(fields as unknown as Voyage[]);
    return voyageIndex;
  };
  const isAiProcess =
    `${getValues("operation")?.[0]}${getValues("modality")?.[0]}` === "IA";

  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.voyages[${getVoyageIndex()}].origin` as `boarding.voyages.0.origin`
        }
        control={control}
        render={({field}) => (
          <CustomAutocomplete
            optionSecondaryContent
            {...field}
            fullWidth
            textFieldProps={{
              label: title,
            }}
            name={
              `boarding.voyages[${getVoyageIndex()}].origin` as `boarding.voyages.0.origin`
            }
            placeholder={title}
            tooltipText={getTooltipText(field.value?.name)}
            loading={isLoadingOptions}
            options={mountOptions(field.value, boardingOptions)}
            value={field.value?.name}
            onOpen={() =>
              getOptions(
                setBoardingOptions,
                search,
                1,
                50,
                getLocationType(isAiProcess, ID_INITIAL_VOYAGE),
              )
            }
            onInputChange={(e, value) => {
              if (e !== null) {
                setSearch(value);
                getOptions(
                  setBoardingOptions,
                  value,
                  1,
                  50,
                  getLocationType(isAiProcess, ID_INITIAL_VOYAGE),
                );
              }
            }}
            getNextPage={() => {
              onBottomScroll(
                setBoardingOptions,
                search,
                getLocationType(isAiProcess, ID_INITIAL_VOYAGE),
              );
            }}
            onChange={(e, value) => {
              handleChangeAutocomplete(
                e,
                value,
                `boarding.voyages[${getVoyageIndex()}].origin`,
              );
            }}
            isOptionEqualToValue={(option, value) => value === option?.label}
          />
        )}
      />
    </Grid>
  );
};

const Destination = (props: Props) => {
  const {xs = 2, title = "Destino"} = props;
  const {control, getValues} = useFormContext<Process>();

  const {fields} = useFieldArray({
    name: "boarding.voyages",
    keyName: "formId",
    control: control,
  });

  const getVoyageIndex = () => {
    return getDestinationVoyageIndex(fields as unknown as Voyage[]);
  };

  const [boardingOptions, setBoardingOptions] = useState(
    [] as AutoCompleteData[],
  );
  const [search, setSearch] = useState("");

  const {getOptions, onBottomScroll, isLoadingOptions} =
    useHandleBoardingOptions();
  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();

  const isAiProcess =
    `${getValues("operation")?.[0]}${getValues("modality")?.[0]}` === "IA";

  //Aqui vou considerar como transbordo pq essa viagem é transbordo ou emb. inicial, o tipo
  //é sempre o mesmo (porto pra IM e aeroporto pra IA)
  const VOYAGE_TYPE_FOR_LOCATION_TYPE = ID_TRANSHIPMENT_TYPE_VOYAGE;

  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.voyages[${getVoyageIndex()}].destination` as `boarding.voyages.0.destination`
        }
        control={control}
        render={({field}) => (
          <CustomAutocomplete
            optionSecondaryContent
            {...field}
            fullWidth
            textFieldProps={{
              label: title,
            }}
            name={
              `boarding.voyages[${getVoyageIndex()}].destination` as `boarding.voyages.0.destination`
            }
            placeholder="Destino"
            tooltipText={getTooltipText(field.value?.name)}
            loading={isLoadingOptions}
            options={mountOptions(field.value, boardingOptions)}
            value={field.value?.name ?? ""}
            onOpen={() =>
              getOptions(
                setBoardingOptions,
                search,
                1,
                50,
                getLocationType(isAiProcess, VOYAGE_TYPE_FOR_LOCATION_TYPE),
              )
            }
            onInputChange={(e, value) => {
              if (e !== null) {
                setSearch(value);
                getOptions(
                  setBoardingOptions,
                  value,
                  1,
                  50,
                  getLocationType(isAiProcess, VOYAGE_TYPE_FOR_LOCATION_TYPE),
                );
              }
            }}
            getNextPage={() => {
              onBottomScroll(
                setBoardingOptions,
                search,
                getLocationType(isAiProcess, VOYAGE_TYPE_FOR_LOCATION_TYPE),
              );
            }}
            onChange={(e, value) => {
              handleChangeAutocomplete(
                e,
                value,
                `boarding.voyages[${getVoyageIndex()}].destination`,
              );
            }}
            isOptionEqualToValue={(option, value) => value === option?.label}
          />
        )}
      />
    </Grid>
  );
};

const FinalDestination = (props: Props) => {
  const {xs = 2, title = "Destino Final"} = props;
  const {control, getValues} = useFormContext<Process>();
  const [boardingOptions, setBoardingOptions] = useState(
    [] as AutoCompleteData[],
  );
  const [search, setSearch] = useState("");

  const {fields} = useFieldArray({
    name: "boarding.voyages",
    keyName: "formId",
    control: control,
  });

  const {getOptions, onBottomScroll, isLoadingOptions} =
    useHandleBoardingOptions();
  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();

  const finalDestinationOrLastTranshipmentIndex = () => {
    if (getFinalDestinationVoyageIndex(fields as unknown as Voyage[]) >= 0) {
      return getFinalDestinationVoyageIndex(fields as unknown as Voyage[]);
    }
    return getDestinationVoyageIndex(fields as unknown as Voyage[]);
  };

  const typeVoyageForLocationType =
    fields?.[finalDestinationOrLastTranshipmentIndex()]?.typeVoyage?.id;

  const isAiProcess =
    `${getValues("operation")?.[0]}${getValues("modality")?.[0]}` === "IA";
  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.voyages[${finalDestinationOrLastTranshipmentIndex()}].destination` as `boarding.voyages.0.destination`
        }
        control={control}
        render={({field}) => (
          <CustomAutocomplete
            optionSecondaryContent
            fullWidth
            textFieldProps={{
              label: title,
            }}
            {...field}
            name={`voyages[${finalDestinationOrLastTranshipmentIndex()}].destination`}
            placeholder="Destino"
            tooltipText={getTooltipText(field.value?.name)}
            loading={isLoadingOptions}
            options={mountOptions(field.value, boardingOptions)}
            value={field.value?.name ?? ""}
            onOpen={() =>
              getOptions(
                setBoardingOptions,
                search,
                1,
                50,
                getLocationType(isAiProcess, typeVoyageForLocationType),
              )
            }
            onInputChange={(e, value) => {
              if (e !== null) {
                setSearch(value);
                getOptions(
                  setBoardingOptions,
                  value,
                  1,
                  50,
                  getLocationType(isAiProcess, typeVoyageForLocationType),
                );
              }
            }}
            getNextPage={() => {
              onBottomScroll(
                setBoardingOptions,
                search,
                getLocationType(isAiProcess, typeVoyageForLocationType),
              );
            }}
            onChange={(e, value) => {
              handleChangeAutocomplete(
                e,
                value,
                `boarding.voyages[${finalDestinationOrLastTranshipmentIndex()}].destination`,
              );
            }}
            isOptionEqualToValue={(option, value) => value === option?.label}
          />
        )}
      />
    </Grid>
  );
};

const TT = (props: Props) => {
  const {xs = 2, title = "TT"} = props;
  const {control, setValue} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.transitTime` as `boarding.transitTime`}
        control={control}
        render={({field: {value}}) => (
          <ProcessTextField
            name={`boarding.transitTime` as `boarding.transitTime`}
            placeholder="TT"
            value={value}
            onChange={(e) =>
              setValue(`boarding.transitTime`, +e.target.value, {
                shouldDirty: true,
              })
            }
            size="small"
            type="number"
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const TTMax = (props: Props) => {
  const {xs = 2, title = "TT Max"} = props;
  const {control, setValue} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.transitTimeMax` as `boarding.transitTimeMax`}
        control={control}
        render={({field: {value}}) => (
          <ProcessTextField
            name={`boarding.transitTimeMax` as `boarding.transitTimeMax`}
            placeholder="TT Max"
            value={value}
            onChange={(e) =>
              setValue(`boarding.transitTimeMax`, +e.target.value, {
                shouldDirty: true,
              })
            }
            inputProps={{
              style: {
                fontSize: 14,
              },
            }}
            size="small"
            type="number"
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const PlacePickup = (props: Props) => {
  const {xs = 2, title = "Local de Coleta Origem"} = props;
  const {control, watch} = useFormContext<Process>();
  const mainVoyageIndex = watch("boarding.voyages")?.findIndex(
    (item) => item?.typeVoyage?.name === "Embarque inicial",
  );

  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.voyages[${mainVoyageIndex}].placePickup` as `boarding.voyages.0.placePickup`
        }
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessTextField
            name={
              `boarding.voyages[${mainVoyageIndex}].placePickup` as `boarding.voyages.0.placePickup`
            }
            placeholder="Local coleta"
            value={value ?? ""}
            onChange={onChange}
            size="small"
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const BookingNumber = (props: Props) => {
  const {xs = 2, title = "N° Booking"} = props;
  const {control} = useFormContext<Process>();
  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.bookingNumber` as `boarding.bookingNumber`}
        control={control}
        rules={{
          validate: (value) => {
            if (value && value?.length > 25) {
              return "Número do booking";
            }
            return true;
          },
        }}
        render={({
          field: {onChange, value},
          formState: {errors, touchedFields},
        }) => (
          <ProcessTextField
            name={`boarding.bookingNumber` as `boarding.bookingNumber`}
            size="small"
            placeholder="N° booking"
            value={value}
            onChange={onChange}
            error={!!errors?.boarding?.bookingNumber}
            helperText={
              touchedFields?.boarding?.bookingNumber && "Máx. 25 caracteres"
            }
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const Incoterm = (props: Props) => {
  const {xs = 2, title = "Incoterm"} = props;
  const {control} = useFormContext<Process>();
  const [incotermOptions, setIncotermOptions] = useState(
    [] as AutoCompleteData[],
  );
  const [search, setSearch] = useState("");

  const {
    getOptions: getIncotermOptions,
    onBottomScroll: onBottomScrollInconterm,
    isLoadingOptions: isLoadingIncotermOptions,
  } = useHandleFieldLists(GetIncotermsList);

  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();

  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.incoterm` as `boarding.incoterm`}
        control={control}
        render={({field: {value}}) => (
          <CustomAutocomplete
            optionSecondaryContent
            fullWidth
            textFieldProps={{
              label: title,
            }}
            name={`boarding.incoterm` as `boarding.incoterm`}
            placeholder="Incoterm"
            tooltipText={getTooltipText(value?.name ?? "")}
            loading={isLoadingIncotermOptions}
            options={mountOptions(value, incotermOptions)}
            value={value?.name ?? ""}
            onOpen={() => getIncotermOptions(setIncotermOptions, 1, search)}
            onInputChange={(e, value) => {
              if (e !== null) {
                setSearch(value);
                getIncotermOptions(setIncotermOptions, 1, search);
              }
            }}
            getNextPage={() => {
              onBottomScrollInconterm(setIncotermOptions, search);
            }}
            onChange={(e, value) => {
              handleChangeAutocomplete(e, value, "boarding.incoterm");
            }}
            isOptionEqualToValue={(option, value) => value === option?.label}
            disabled
          />
        )}
      />
    </Grid>
  );
};

const TransportCompany = (props: Props) => {
  const {xs = 2, title = "Companhia de Transporte"} = props;
  const {control} = useFormContext<Process>();
  const [transportCompanyOptions, setTransportCompanyOptions] = useState(
    [] as AutoCompleteData[],
  );

  const [search, setSearch] = useState("");

  const {
    getOptions: getTransportCompanyOptions,
    onBottomScroll: onBottomScrollTransportCompany,
    isLoadingOptions: isLoadingTransportCompanyOptions,
  } = useHandleFieldLists(GetTransportCompaniesList, formatTransportCompanyFn);

  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();
  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.transportCompany` as `boarding.transportCompany`}
        control={control}
        render={({field: {value}}) => (
          <CustomAutocomplete
            disabled
            optionSecondaryContent
            fullWidth
            textFieldProps={{
              label: title,
            }}
            name={`boarding.transportCompany` as `boarding.transportCompany`}
            placeholder="Companhia"
            tooltipText={getTooltipText(value?.name ?? "")}
            loading={isLoadingTransportCompanyOptions}
            options={mountOptions(value, transportCompanyOptions)}
            value={value?.name ?? null}
            onOpen={() =>
              getTransportCompanyOptions(setTransportCompanyOptions, 1, search)
            }
            onInputChange={(e, value) => {
              if (e !== null) {
                setSearch(value);
                getTransportCompanyOptions(
                  setTransportCompanyOptions,
                  1,
                  value,
                );
              }
            }}
            getNextPage={() => {
              onBottomScrollTransportCompany(
                setTransportCompanyOptions,
                search,
              );
            }}
            onChange={(e, value) => {
              handleChangeAutocomplete(e, value, "boarding.transportCompany");
            }}
            isOptionEqualToValue={(option, value) => value === option?.label}
          />
        )}
      />
    </Grid>
  );
};

const TypePath = (props: Props) => {
  const {xs = 2, title = "Tipo de Rota"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.typePath` as `boarding.typePath`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessSelect
            data={Object.values(TypePathEnum)}
            label={title}
            value={value}
            name={`boarding.typePath` as `boarding.typePath`}
            onChange={onChange}
            required
            size="small"
          />
        )}
      />
    </Grid>
  );
};

const TypeLoad = (props: Props) => {
  const {xs = 2, title = "Modalidade"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`processLoads.0.typeLoad` as `processLoads.0.typeLoad`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessSelect
            name={`processLoads.0.typeLoad` as `processLoads.0.typeLoad`}
            value={value}
            onChange={onChange}
            data={Object.values(TypeLoadEnum)}
            size="small"
            disabled
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const Path = (props: Props) => {
  const {xs = 2, title = "Rota/Serviço"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.path` as `boarding.path`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessTextField
            size="small"
            name={`boarding.path` as `boarding.path`}
            placeholder="Rota"
            value={value}
            onChange={onChange}
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const DateEstimatedReadinessCargo = (props: Props) => {
  const {xs = 2, title = "Prev. de prontidão"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.dateEstimatedReadinessCargo` as `boarding.dateEstimatedReadinessCargo`
        }
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.dateEstimatedReadinessCargo` as `boarding.dateEstimatedReadinessCargo`
            }
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const DateReadinessCargo = (props: Props) => {
  const {xs = 2, title = "Conf. de prontidão"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.dateReadinessCargo` as `boarding.dateReadinessCargo`}
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.dateReadinessCargo` as `boarding.dateReadinessCargo`
            }
            title="Confirmação de prontidão"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

/*
  O ETD do boarding é o campo ETA da viagem do 
  tipo "Embarque inicial"
*/
const ETD = (props: Props) => {
  const {xs = 2, title = "ETD"} = props;
  const {control} = useFormContext<Process>();

  const fields = useWatch({
    control,
    name: "boarding.voyages",
  });

  const getVoyageIndex = () => {
    return getInitialVoyageIndex(fields as unknown as Voyage[]);
  };
  return (
    <Grid size={xs}>
      <Controller
        key={
          `boarding.voyages.${getVoyageIndex()}.estimatedCargoLoad` as `boarding.voyages.0.estimatedCargoLoad`
        }
        name={
          `boarding.voyages.${getVoyageIndex()}.estimatedCargoLoad` as `boarding.voyages.0.estimatedCargoLoad`
        }
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.voyages.${getVoyageIndex()}.estimatedCargoLoad` as `boarding.voyages.0.estimatedCargoLoad`
            }
            title="ETD"
            errorName="ETD"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

/*
  O ETA do boarding é o campo ETA da viagem do 
  tipo "Transbordo/Escala" com destino = destino do boarding
*/
const ETA = (props: Props) => {
  const {xs = 2, title = "ETA"} = props;
  const {control} = useFormContext<Process>();

  const fields = useWatch({
    control,
    name: "boarding.voyages",
  });

  const getVoyageIndex = () => {
    return getDestinationVoyageIndex(fields as unknown as Voyage[]);
  };

  return (
    <Grid size={xs}>
      <Controller
        key={
          `boarding.voyages.${getVoyageIndex()}.estimatedCargoUnload` as `boarding.voyages.0.estimatedCargoUnload`
        }
        name={
          `boarding.voyages.${getVoyageIndex()}.estimatedCargoUnload` as `boarding.voyages.0.estimatedCargoUnload`
        }
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.voyages.${getVoyageIndex()}.estimatedCargoUnload` as `boarding.voyages.0.estimatedCargoUnload`
            }
            title="ETA"
            errorName="ETA"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const ModalityPickup = (props: Props) => {
  const {xs = 2, title = "Modalidade de coleta"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`modalityPickup` as `modalityPickup`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessSelect
            data={Object.values(ModalityPickupEnum)}
            label={title}
            value={value}
            name={`modalityPickup` as `modalityPickup`}
            onChange={onChange}
            required
            size="small"
          />
        )}
      />
    </Grid>
  );
};

const ModalityDelivery = (props: Props) => {
  const {xs = 2, title = "Modalidade de entrega"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`modalityDelivery` as `modalityDelivery`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessSelect
            data={Object.values(ModalityDeliveryEnum)}
            label={title}
            value={value}
            name={`modalityDelivery` as `modalityDelivery`}
            onChange={onChange}
            required
            size="small"
          />
        )}
      />
    </Grid>
  );
};

/**
 * Estamos considerando aqui como a data estimada de chegada (etd) da ultima viagem.
 */
const DateEstimatedDelivery = (props: Props) => {
  const {xs = 2, title = "Data est. de Entrega"} = props;
  const {control} = useFormContext<Process>();
  const fields = useWatch({
    control,
    name: "boarding.voyages",
  });

  const lastVoyageIndex = fields?.length - 1;

  return (
    <Grid size={xs}>
      <Controller
        key={
          `boarding.voyages.${lastVoyageIndex}.estimatedCargoUnload` as `boarding.voyages.0.estimatedCargoUnload`
        }
        name={
          `boarding.voyages.${lastVoyageIndex}.estimatedCargoUnload` as `boarding.voyages.0.estimatedCargoUnload`
        }
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.voyages.${lastVoyageIndex}.estimatedCargoUnload` as `boarding.voyages.0.estimatedCargoUnload`
            }
            title="Data est. de Entrega"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const DateDelivery = (props: Props) => {
  const {xs = 2, title = "Data de entrega"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`dateDelivery` as `dateDelivery`}
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={`dateDelivery` as `dateDelivery`}
            title="Data de entrega"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const DateDeliveryOrigin = (props: Props) => {
  const {xs = 2, title = "Data de entrega na origem"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`dateDeliveryOrigin` as `dateDeliveryOrigin`}
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={`dateDeliveryOrigin` as `dateDeliveryOrigin`}
            title="Data de entrega na origem"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const PlaceDeliveryOrigin = (props: Props) => {
  const {xs = 2, title = "Local de recebimento Origem"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`placeDeliveryOrigin` as `placeDeliveryOrigin`}
        control={control}
        render={({field: {value, onChange}}) => (
          <ProcessTextField
            size="small"
            name={`placeDeliveryOrigin` as `placeDeliveryOrigin`}
            placeholder="Local de recebimento Origem"
            value={value ?? ""}
            onChange={onChange}
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const ReceivingLocation = (props: Props) => {
  const {xs = 2, title = "Local de recebimento"} = props;
  const {control} = useFormContext<Process>();
  const [placeDeliveryDestinationOptions, setPlaceDeliveryDestinationOptions] =
    useState([] as AutoCompleteData[]);

  const [searchFields, setSearchFields] = useState({
    placeDeliveryOrigin: "",
    placeDeliveryDestination: "",
    transportCompany: "",
  });

  const {
    getOptions: getPlaceDeliveryDestinationOptions,
    onBottomScroll: onBottomScrollPlaceDeliveryDestination,
    isLoadingOptions: isLoadingPlaceDeliveryDestinationOptions,
  } = useHandleFieldLists(getPorts, formatOptionsFn);

  const {handleChangeAutocomplete} = useChangeDAteAndAutocomplete();
  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.receivingLocation` as `boarding.receivingLocation`}
        control={control}
        render={({field: {value}}) => (
          <CustomAutocomplete
            optionSecondaryContent
            textFieldProps={{
              label: title,
            }}
            size="small"
            name={`boarding.receivingLocation` as `boarding.receivingLocation`}
            placeholder="Local de recebimento"
            loading={isLoadingPlaceDeliveryDestinationOptions}
            value={value?.name ?? ""}
            tooltipText={getTooltipText(value?.name ?? "")}
            options={placeDeliveryDestinationOptions}
            onChange={(e, value) => {
              handleChangeAutocomplete(e, value, "boarding.receivingLocation");
            }}
            onOpen={() =>
              getPlaceDeliveryDestinationOptions(
                setPlaceDeliveryDestinationOptions,
              )
            }
            getNextPage={() => {
              onBottomScrollPlaceDeliveryDestination(
                setPlaceDeliveryDestinationOptions,
                searchFields.placeDeliveryDestination,
              );
            }}
            isOptionEqualToValue={(option, value) => value === option?.label}
            onInputChange={(e, value) => {
              //condição pra não fazer a requisição na primeira atualização do input
              if (e !== null) {
                setSearchFields((prev) => ({
                  ...prev,
                  placeDeliveryDestination: value,
                }));
                getPlaceDeliveryDestinationOptions(
                  setPlaceDeliveryDestinationOptions,
                  1,
                  value,
                );
              }
            }}
          />
        )}
      />
    </Grid>
  );
};

const DateCargoLoad = (props: Props) => {
  const {xs = 2, title = "Confirmação de embarque"} = props;
  const {control} = useFormContext<Process>();

  const {fields} = useFieldArray({
    name: "boarding.voyages",
    keyName: "formId",
    control: control,
  });

  const getVoyageIndex = () => {
    return getInitialVoyageIndex(fields as unknown as Voyage[]);
  };

  const voyageWithCargoUnload = !!fields?.[getVoyageIndex()]?.cargoUnload;

  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.voyages.${getVoyageIndex()}.cargoLoad` as `boarding.voyages.0.cargoLoad`
        }
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.voyages.${getVoyageIndex()}.cargoLoad` as `boarding.voyages.0.cargoLoad`
            }
            title="Confirmação de embarque"
            // disabled={voyageWithCargoUnload && !!value}
            disabled
            errorName="Embarque"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const DateCargoUnload = (props: Props) => {
  const {xs = 2, title = "Conf. de desembarque"} = props;
  const {control} = useFormContext<Process>();

  const {fields} = useFieldArray({
    name: "boarding.voyages",
    keyName: "formId",
    control: control,
  });

  const getVoyageIndex = () => {
    return getDestinationVoyageIndex(fields as unknown as Voyage[]);
  };

  const voyageWithoutCargoLoad = !fields?.[getVoyageIndex()]?.cargoLoad;

  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.voyages.${getVoyageIndex()}.cargoUnload` as `boarding.voyages.0.cargoUnload`
        }
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.voyages.${getVoyageIndex()}.cargoUnload` as `boarding.voyages.0.cargoUnload`
            }
            title="Confirmação de desembarque"
            // disabled={voyageWithoutCargoLoad}
            disabled
            errorName="Desembarque"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const DateEstimatedPickup = (props: Props) => {
  const {xs = 2, title = "Data estimada de coleta"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`dateEstimatedPickup` as `dateEstimatedPickup`}
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={`dateEstimatedPickup` as `dateEstimatedPickup`}
            title="Data estimada de coleta"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const DatePickup = (props: Props) => {
  const {xs = 2, title = "Confirmação de coleta"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`datePickup` as `datePickup`}
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={`datePickup` as `datePickup`}
            title="Confirmação de coleta"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const TerminalMooring = (props: Props) => {
  const {xs = 2, title = "Terminal de chegada"} = props;
  const {control} = useFormContext<Process>();
  const [mooringOptions, setMooringOptions] = useState<AutoCompleteData[]>(
    [] as AutoCompleteData[],
  );

  const [search, setSearch] = useState("");

  const {
    getOptions: getMooringOptions,
    onBottomScroll: onBottomScrollMooring,
    isLoadingOptions: isLoadingMooringOptions,
  } = useHandleFieldLists(GetPersonsByTypes, null, [
    PersonTypes.containerTerminal,
  ]);

  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();
  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.terminalMooring` as `boarding.terminalMooring`}
        control={control}
        render={({field: {value}}) => (
          <CustomAutocomplete
            optionSecondaryContent
            textFieldProps={{
              label: title,
            }}
            name={`boarding.terminalMooring` as `boarding.terminalMooring`}
            placeholder="Terminal de chegada"
            tooltipText={getTooltipText(value?.name ?? "")}
            loading={isLoadingMooringOptions}
            options={mountOptions(value, mooringOptions)}
            value={value?.name ?? ""}
            onOpen={() => getMooringOptions(setMooringOptions, 1, search)}
            onInputChange={(e, value) => {
              if (e !== null) {
                setSearch(value);
                getMooringOptions(setMooringOptions, 1, value);
              }
            }}
            getNextPage={() => {
              onBottomScrollMooring(setMooringOptions, search);
            }}
            onChange={(e, value) => {
              handleChangeAutocomplete(e, value, "boarding.terminalMooring");
            }}
            isOptionEqualToValue={(option, value) => value === option?.label}
          />
        )}
      />
    </Grid>
  );
};

const TerminalRedestination = (props: Props) => {
  const {xs = 2, title = "Terminal de redestinação"} = props;
  const {control} = useFormContext<Process>();
  const [redestinationOptions, setRedestinationOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);
  const [search, setSearch] = useState("");
  const {
    getOptions: getRedestinationOptions,
    onBottomScroll: onBottomScrollRedestination,
    isLoadingOptions: isLoadingRedestinationOptions,
  } = useHandleFieldLists(GetPersonsByTypes, null, [
    PersonTypes.containerTerminal,
  ]);

  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();
  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.terminalRedestination` as `boarding.terminalRedestination`
        }
        control={control}
        render={({field: {value}}) => (
          <CustomAutocomplete
            optionSecondaryContent
            textFieldProps={{
              label: title,
            }}
            name={
              `boarding.terminalRedestination` as `boarding.terminalRedestination`
            }
            placeholder="Terminal de chegada"
            tooltipText={getTooltipText(value?.name ?? "")}
            loading={isLoadingRedestinationOptions}
            options={mountOptions(value, redestinationOptions)}
            value={value?.name ?? ""}
            onOpen={() =>
              getRedestinationOptions(setRedestinationOptions, 1, search)
            }
            onInputChange={(e, value) => {
              if (e !== null) {
                setSearch(value);
                getRedestinationOptions(setRedestinationOptions, 1, value);
              }
            }}
            getNextPage={() => {
              onBottomScrollRedestination(setRedestinationOptions, search);
            }}
            onChange={(e, value) => {
              handleChangeAutocomplete(
                e,
                value,
                "boarding.terminalRedestination",
              );
            }}
            isOptionEqualToValue={(option, value) => value === option?.label}
          />
        )}
      />
    </Grid>
  );
};

const DateSpawning = (props: Props) => {
  const {xs = 2, title = "Desova"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        key={`processLoads.0.dateSpawning` as `processLoads.0.dateSpawning`}
        name={`processLoads.0.dateSpawning` as `processLoads.0.dateSpawning`}
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            name={
              `processLoads.0.dateSpawning` as `processLoads.0.dateSpawning`
            }
            value={value}
            title="Desova"
            errorName="Desova"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

//Campos Marelli

/*
 * ATA = Actual Time of Arrival
 */
const ActualTimeOfArrival = (props: Props) => {
  const {xs = 2, title = "ATA"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.dateActualTimeArrival` as `boarding.dateActualTimeArrival`
        }
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.dateActualTimeArrival` as `boarding.dateActualTimeArrival`
            }
            title="ATA"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

/**
 * ATB = Actual Time of Berthing
 */
const ActualTimeOfBerthing = (props: Props) => {
  const {xs = 2, title = "ATB"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.dateActualTimeBerthing` as `boarding.dateActualTimeBerthing`
        }
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.dateActualTimeBerthing` as `boarding.dateActualTimeBerthing`
            }
            title="ATB"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const TargetETD = (props: Props) => {
  const {xs = 2, title = "ETD Target"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.dateTargetEstimatedCargoLoad` as `boarding.dateTargetEstimatedCargoLoad`
        }
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.dateTargetEstimatedCargoLoad` as `boarding.dateTargetEstimatedCargoLoad`
            }
            title="ETD Target"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const TargetETA = (props: Props) => {
  const {xs = 2, title = "ETA Target"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={
          `boarding.dateTargetEstimatedCargoUnload` as `boarding.dateTargetEstimatedCargoUnload`
        }
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={
              `boarding.dateTargetEstimatedCargoUnload` as `boarding.dateTargetEstimatedCargoUnload`
            }
            title="ETA Target"
            label={title}
            fullWidth
          />
        )}
      />
    </Grid>
  );
};

const PlaceDelivery = (props: Props) => {
  const {xs = 2, title = "Local de entrega"} = props;
  const {control} = useFormContext<Process>();

  //Campo do processo, ver se o back calcula isso de alguma forma, ou é preenchido, sei lá
  return (
    <Grid size={xs}>
      <Controller
        name={`placeDelivery`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessTextField
            name={`placeDelivery`}
            placeholder="Local de entrega"
            value={value ?? ""}
            onChange={onChange}
            size="small"
            fullWidth
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const RaFinalDestination = (props: Props) => {
  const {xs = 2, title = "Recinto Aduaneiro"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.raFinalDestination` as `boarding.raFinalDestination`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessTextField
            name={
              `boarding.raFinalDestination` as `boarding.raFinalDestination`
            }
            placeholder="Recinto Aduaneiro"
            value={value ?? ""}
            onChange={onChange}
            size="small"
            fullWidth
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const RaCode = (props: Props) => {
  const {xs = 2, title = "Código Recinto Aduaneiro"} = props;
  const {control} = useFormContext<Process>();

  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.raCode` as `boarding.raCode`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessTextField
            name={`boarding.raCode` as `boarding.raCode`}
            placeholder="Código Recinto Aduaneiro"
            value={value ?? ""}
            onChange={onChange}
            size="small"
            fullWidth
            label={title}
          />
        )}
      />
    </Grid>
  );
};

//nar

const Nar = (props: Props) => {
  const {xs = 2, title = "NAR"} = props;
  const {control} = useFormContext<Process>();
  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.isNar` as `boarding.isNar`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessSelect
            data={[
              {name: "SIM", value: true},
              {name: "NÃO", value: false},
            ]}
            label={title}
            value={value}
            name={`boarding.isNar` as `boarding.isNar`}
            onChange={onChange}
            size="small"
          />
        )}
      />
    </Grid>
  );
};

//li
const Li = (props: Props) => {
  const {xs = 2, title = "LI"} = props;
  const {control} = useFormContext<Process>();
  const loadsIndex = 0;

  return (
    <Grid size={xs}>
      <Controller
        name={
          `processLoads.${loadsIndex}.isImportLicense` as `processLoads.0.isImportLicense`
        }
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessSelect
            data={[
              {name: "SIM", value: true},
              {name: "NÃO", value: false},
            ]}
            label={title}
            value={value}
            name={
              `processLoads.${loadsIndex}.isImportLicense` as `processLoads.0.isImportLicense`
            }
            onChange={onChange}
            size="small"
          />
        )}
      />
    </Grid>
  );
};

//necessidade isca
const Isca = (props: Props) => {
  const {xs = 2, title = "ISCA"} = props;

  const {control, watch} = useFormContext<Process>();
  const voyages = watch("boarding.voyages");

  const hasDtaVoyage = voyages?.some(
    (voyage) => voyage?.typeVoyage?.id === ID_FINAL_DESTINATION_VOYAGE,
  );

  if (!hasDtaVoyage) {
    return null;
  }
  const loadsIndex = 0;

  return (
    <Grid size={xs}>
      <Controller
        name={
          `processLoads.${loadsIndex}.isDecoyCargo` as `processLoads.0.isDecoyCargo`
        }
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessSelect
            data={[
              {name: "SIM", value: true},
              {name: "NÃO", value: false},
            ]}
            label={title}
            value={value}
            name={
              `processLoads.${loadsIndex}.isDecoyCargo` as `processLoads.0.isDecoyCargo`
            }
            onChange={onChange}
            size="small"
          />
        )}
      />
    </Grid>
  );
};

//necessidade escolta
const Escolta = (props: Props) => {
  const {xs = 2, title = "Escolta"} = props;

  const {control, watch} = useFormContext<Process>();
  const voyages = watch("boarding.voyages");

  const hasDtaVoyage = voyages?.some(
    (voyage) => voyage?.typeVoyage?.id === ID_FINAL_DESTINATION_VOYAGE,
  );

  if (!hasDtaVoyage) {
    return null;
  }

  const loadsIndex = 0;

  return (
    <Grid size={xs}>
      <Controller
        name={
          `processLoads.${loadsIndex}.isEscortCargo` as `processLoads.0.isEscortCargo`
        }
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessSelect
            data={[
              {name: "SIM", value: true},
              {name: "NÃO", value: false},
            ]}
            label={title}
            value={value}
            name={
              `processLoads.${loadsIndex}.isEscortCargo` as `processLoads.0.isEscortCargo`
            }
            onChange={onChange}
            size="small"
          />
        )}
      />
    </Grid>
  );
};

const TransitTimePickup = (props: Props) => {
  const {xs = 2, title = "TT Coleta"} = props;
  const {control, watch} = useFormContext<Process>();
  const voyages = watch("boarding.voyages");

  const hasPickupVoyage = voyages?.some(
    (voyage) => voyage?.typeVoyage?.id === ID_COLLECT_VOYAGE,
  );

  if (!hasPickupVoyage) {
    return null;
  }

  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.transitTimePickup` as `boarding.transitTimePickup`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessTextField
            name={`boarding.transitTimePickup` as `boarding.transitTimePickup`}
            placeholder="TT Coleta"
            value={value ?? ""}
            onChange={onChange}
            size="small"
            fullWidth
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const TransitTimeDTA = (props: Props) => {
  const {xs = 2, title = "TT DTA"} = props;
  const {control, watch} = useFormContext<Process>();
  const voyages = watch("boarding.voyages");

  const hasDtaVoyage = voyages?.some(
    (voyage) => voyage?.typeVoyage?.id === ID_FINAL_DESTINATION_VOYAGE,
  );

  if (!hasDtaVoyage) {
    return null;
  }

  return (
    <Grid size={xs}>
      <Controller
        name={`boarding.transitTimeDta` as `boarding.transitTimeDta`}
        control={control}
        render={({field: {onChange, value}}) => (
          <ProcessTextField
            name={`boarding.transitTimeDta` as `boarding.transitTimeDta`}
            placeholder="TT DTA"
            value={value ?? ""}
            onChange={onChange}
            size="small"
            fullWidth
            label={title}
          />
        )}
      />
    </Grid>
  );
};

const DateGateIn = (props: Props) => {
  const {xs = 2, title = "Data de gate in"} = props;
  const {control, getValues} = useFormContext<Process>();
  /**
   * Adotando default 1 pq o House geralmente é o index 1.
   */
  const DEFAULT_HOUSE_INDEX = 1;
  /**
   * Aqui, mandaremos isso sempre no index 0, o backend vai replicar pra todos no back do iNext e HC.
   */
  const index = 0;
  const indexOfHouse =
    getValues("knowledges")?.findIndex(
      (item) => item.typeKnowledge === "House",
    ) ?? DEFAULT_HOUSE_INDEX;

  const rootName =
    `knowledges[${indexOfHouse}].logisticBillLading.logisticBillLadingLoads` as `knowledges.0.logisticBillLading.logisticBillLadingLoads`;

  return (
    <Grid size={xs}>
      <Controller
        name={`${rootName}.${index}.dateGateIn`}
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={`${rootName}.${index}.dateGateIn`}
            title={title}
            label={title}
            fullWidth
            disabled
          />
        )}
      />
    </Grid>
  );
};

const DateGateOut = (props: Props) => {
  const {xs = 2, title = "Data de gate out"} = props;
  const {control, getValues} = useFormContext<Process>();
  /**
   * Adotando default 1 pq o House geralmente é o index 1.
   */
  const DEFAULT_HOUSE_INDEX = 1;
  /**
   * Aqui, mandaremos isso sempre no index 0, o backend vai replicar pra todos no back do iNext e HC.
   */
  const index = 0;
  const indexOfHouse =
    getValues("knowledges")?.findIndex(
      (item) => item.typeKnowledge === "House",
    ) ?? DEFAULT_HOUSE_INDEX;

  const rootName =
    `knowledges[${indexOfHouse}].logisticBillLading.logisticBillLadingLoads` as `knowledges.0.logisticBillLading.logisticBillLadingLoads`;

  return (
    <Grid size={xs}>
      <Controller
        name={`${rootName}.${index}.dateGateOut`}
        control={control}
        render={({field: {value}}) => (
          <ProcessDatePicker
            value={value}
            name={`${rootName}.${index}.dateGateOut`}
            title={title}
            label={title}
            fullWidth
            disabled
          />
        )}
      />
    </Grid>
  );
};

const fclCase = [
  {item: Origin, xs: 2.4, id: crypto.randomUUID()},
  {item: Destination, xs: 2.4, id: crypto.randomUUID()},
  {item: FinalDestination, xs: 2.4, id: crypto.randomUUID()},
  {item: TypePath, xs: 2.4, id: crypto.randomUUID()},
  {item: Path, xs: 2.4, id: crypto.randomUUID()},
  {item: TypeLoad, xs: 2.4, id: crypto.randomUUID()}, //Modalidade
  {item: Incoterm, xs: 2, id: crypto.randomUUID()},
  {item: BookingNumber, xs: 1.7, id: crypto.randomUUID()},
  {item: TT, xs: 1.1, id: crypto.randomUUID()},
  {item: TransportCompany, xs: 2.4, id: crypto.randomUUID()},
  {item: PlacePickup, xs: 2.4, id: crypto.randomUUID()},
  {item: PlaceDeliveryOrigin, xs: 2.4, id: crypto.randomUUID()},
  {item: DateEstimatedReadinessCargo, xs: 2.4, id: crypto.randomUUID()},
  {item: DateReadinessCargo, xs: 2.4, id: crypto.randomUUID()},
  {item: DateEstimatedPickup, xs: 2.4, id: crypto.randomUUID()},
  {item: DatePickup, xs: 2.4, id: crypto.randomUUID()},
  {item: ModalityPickup, xs: 2.4, id: crypto.randomUUID()},
  {item: ETD, xs: 2.4, id: crypto.randomUUID()},
  {item: DateCargoLoad, xs: 2.4, id: crypto.randomUUID()},
  {item: ETA, xs: 2.4, id: crypto.randomUUID()},
  {item: DateCargoUnload, xs: 2.4, id: crypto.randomUUID()},
  {item: ActualTimeOfArrival, xs: 2.4, id: crypto.randomUUID()},
  {item: ActualTimeOfBerthing, xs: 2.4, id: crypto.randomUUID()},
  {item: TargetETD, xs: 2.4, id: crypto.randomUUID()},
  {item: TargetETA, xs: 2.4, id: crypto.randomUUID()},
  {item: TerminalMooring, xs: 4, id: crypto.randomUUID()},
  {item: TerminalRedestination, xs: 4, id: crypto.randomUUID()},
  {item: DateGateIn, xs: 2, id: crypto.randomUUID()},
  {item: DateGateOut, xs: 2, id: crypto.randomUUID()},
];

const mainCase = [
  {item: Origin, xs: 2.4, id: crypto.randomUUID()},
  {item: Destination, xs: 2.4, id: crypto.randomUUID()},
  {item: FinalDestination, xs: 2.4, id: crypto.randomUUID()},
  {item: TT, xs: 1, id: crypto.randomUUID()},
  {item: BookingNumber, xs: 1.8, id: crypto.randomUUID()},
  {item: Incoterm, xs: 2, id: crypto.randomUUID()},
  {item: TransportCompany, xs: 2.4, id: crypto.randomUUID()},
  {item: TypePath, xs: 2.4, id: crypto.randomUUID()},
  {item: Path, xs: 2.4, id: crypto.randomUUID()},
  {item: DateEstimatedReadinessCargo, xs: 2.4, id: crypto.randomUUID()},
  {item: DateReadinessCargo, xs: 2.4, id: crypto.randomUUID()},
  {item: ETD, xs: 2.4, id: crypto.randomUUID()},
  {item: DateCargoLoad, xs: 2.4, id: crypto.randomUUID()},
  {item: ETA, xs: 2.4, id: crypto.randomUUID()},
  {item: DateCargoUnload, xs: 2.4, id: crypto.randomUUID()},
  {item: ModalityPickup, xs: 2.4, id: crypto.randomUUID()},
  {item: ModalityDelivery, xs: 2.4, id: crypto.randomUUID()},
  {item: DateEstimatedDelivery, xs: 2.4, id: crypto.randomUUID()},
  {item: DateDelivery, xs: 2.4, id: crypto.randomUUID()},
  {item: DateDeliveryOrigin, xs: 2.4, id: crypto.randomUUID()},
  {item: PlaceDeliveryOrigin, xs: 2.4, id: crypto.randomUUID()},
  {item: ReceivingLocation, xs: 2.4, id: crypto.randomUUID()},
  {item: DateEstimatedPickup, xs: 2.4, id: crypto.randomUUID()}, //caso exw
  {item: PlacePickup, xs: 2.4, id: crypto.randomUUID()},
  {item: DatePickup, xs: 2.4, id: crypto.randomUUID()}, //caso exw
  {item: TypeLoad, xs: 2.4, id: crypto.randomUUID()}, //Modalidade
  {item: ActualTimeOfArrival, xs: 2.4, id: crypto.randomUUID()},
  {item: ActualTimeOfBerthing, xs: 2.4, id: crypto.randomUUID()},
  {item: TargetETD, xs: 2.4, id: crypto.randomUUID()},
  {item: TargetETA, xs: 2.4, id: crypto.randomUUID()},
  {item: DateSpawning, xs: 2.4, id: crypto.randomUUID()},
  {item: TerminalMooring, xs: 4, id: crypto.randomUUID()},
  {item: TerminalRedestination, xs: 4, id: crypto.randomUUID()},
];

const airImportMainCase = [
  {item: Origin, xs: 2.4, id: crypto.randomUUID(), title: "Origem"},
  {item: Destination, xs: 2.4, id: crypto.randomUUID()},
  {item: FinalDestination, xs: 2.4, id: crypto.randomUUID()},
  {item: RaFinalDestination, xs: 2.4, id: crypto.randomUUID()},
  {item: RaCode, xs: 2.4, id: crypto.randomUUID()},
  {item: Incoterm, xs: 2.4, id: crypto.randomUUID()},
  {item: TransportCompany, xs: 2.4, id: crypto.randomUUID()},
  {item: Path, xs: 2.4, id: crypto.randomUUID()},
  {item: ModalityPickup, xs: 2.4, id: crypto.randomUUID()},
  {item: DateEstimatedReadinessCargo, xs: 2.4, id: crypto.randomUUID()},
  {item: DateReadinessCargo, xs: 2.4, id: crypto.randomUUID()},
  {item: DateEstimatedDelivery, xs: 2.4, id: crypto.randomUUID()},
  {item: TT, xs: 1.2, id: crypto.randomUUID()},
  {item: TTMax, xs: 1.2, id: crypto.randomUUID()},
  //so se existe viagem DTA
  {item: TransitTimeDTA, xs: 2.4, id: crypto.randomUUID()},
  //so se existe viagem de coleta
  {item: TransitTimePickup, xs: 2.4, id: crypto.randomUUID()},
  {item: TypeLoad, xs: 2.4, id: crypto.randomUUID()}, //Modalidade
  {item: Nar, xs: 2.4, id: crypto.randomUUID()},
  {item: Li, xs: 2.4, id: crypto.randomUUID()},

  //Só DTA ISca e Escolta
  {item: Isca, xs: 2.4, id: crypto.randomUUID()},
  {item: Escolta, xs: 2.4, id: crypto.randomUUID()},

  {item: TerminalMooring, xs: 4, id: crypto.randomUUID()},
  {item: TerminalRedestination, xs: 4, id: crypto.randomUUID()},
];

const useHandleBoardingFields = () => {
  return {fclCase, mainCase, airImportMainCase};
};

export default useHandleBoardingFields;
